import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/Card';
import { LoadingSpinner } from './LoadingSpinner';
import { Button } from './ui/Button';
import Modal from './ui/Modal';
import { ListeningTimeChart } from './analytics/ListeningTimeChart';
import { TopContentStats } from './analytics/TopContentStats';
import { ListeningStreaks } from './analytics/ListeningStreaks';
import { ActivityFeed } from './analytics/ActivityFeed';
import { YearWrapped } from './analytics/YearWrapped';
import { formatDuration, formatTime } from '../utils/format';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import TimerIcon from '@mui/icons-material/Timer';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface DashboardStats {
  totalListeningTime: number;
  totalSongs: number;
  totalCompleted: number;
  currentStreak: number;
  longestStreak: number;
  favoriteGenre: string | null;
  favoriteArtist: string | null;
  firstListeningDate: string | null;
  lastListeningDate: string | null;
  periodStats: {
    songsPlayed: number;
    songsCompleted: number;
    totalTime: number;
  };
  recentActivity: Array<{
    stream_date: string;
    stream_duration: number;
    song_title: string;
    artist_name: string;
    completed: boolean;
  }>;
}

export function AnalyticsDashboard() {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [showWrapped, setShowWrapped] = useState(false);
  const [showDataExport, setShowDataExport] = useState(false);

  // Fetch dashboard stats
  const { data: stats, isLoading, error, refetch } = useQuery<DashboardStats>({
    queryKey: ['analytics', 'dashboard', selectedPeriod],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/analytics/dashboard?period=${selectedPeriod}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }
      
      return response.json();
    },
    enabled: !!user,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  const handleDataExport = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/analytics/export', {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to export data');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `naia-listening-data-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      setShowDataExport(false);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-pearl-white mb-4">Sign In Required</h2>
          <p className="text-slate-grey">Please sign in to view your listening analytics.</p>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-red-400 mb-4">Error Loading Analytics</h2>
          <p className="text-slate-grey mb-4">There was a problem loading your analytics data.</p>
          <Button onClick={() => refetch()}>Try Again</Button>
        </Card>
      </div>
    );
  }

  const getCompletionRate = () => {
    if (!stats?.totalSongs) return 0;
    return Math.round((stats.totalCompleted / stats.totalSongs) * 100);
  };

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case '90d': return 'Last 3 Months';
      case '1y': return 'Last Year';
      default: return 'Last 30 Days';
    }
  };

  return (
    <div className="min-h-screen bg-dark-void p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-pearl-white mb-2">Your Music Analytics</h1>
            <p className="text-slate-grey">
              Discover your listening patterns and music journey
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Period Selector */}
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as any)}
              className="bg-dark-void border border-white/20 rounded-lg px-4 py-2 text-pearl-white focus:border-naia-pink focus:outline-none"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 3 Months</option>
              <option value="1y">Last Year</option>
            </select>

            {/* Action Buttons */}
            <Button
              onClick={() => setShowWrapped(true)}
              className="bg-gradient-to-r from-naia-pink to-soft-violet"
            >
              <WhatshotIcon className="mr-2" />
              Year Wrapped
            </Button>
            
            <Button
              onClick={() => setShowDataExport(true)}
              variant="outline"
            >
              <DownloadIcon className="mr-2" />
              Export Data
            </Button>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-naia-pink/20 to-soft-violet/20 border-naia-pink/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-grey text-sm">Total Listening Time</p>
                <p className="text-2xl font-bold text-pearl-white">
                  {formatDuration(stats?.totalListeningTime || 0)}
                </p>
              </div>
              <TimerIcon className="text-naia-pink text-3xl" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-cyber-teal/20 to-electric-blue/20 border-cyber-teal/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-grey text-sm">Songs Played</p>
                <p className="text-2xl font-bold text-pearl-white">
                  {stats?.totalSongs?.toLocaleString() || 0}
                </p>
                <p className="text-cyber-teal text-xs">
                  {getCompletionRate()}% completion rate
                </p>
              </div>
              <MusicNoteIcon className="text-cyber-teal text-3xl" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-electric-blue/20 to-soft-violet/20 border-electric-blue/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-grey text-sm">Current Streak</p>
                <p className="text-2xl font-bold text-pearl-white">
                  {stats?.currentStreak || 0} days
                </p>
                <p className="text-electric-blue text-xs">
                  Best: {stats?.longestStreak || 0} days
                </p>
              </div>
              <TrendingUpIcon className="text-electric-blue text-3xl" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-soft-violet/20 to-naia-pink/20 border-soft-violet/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-grey text-sm">{getPeriodLabel()}</p>
                <p className="text-2xl font-bold text-pearl-white">
                  {formatDuration(stats?.periodStats?.totalTime || 0)}
                </p>
                <p className="text-soft-violet text-xs">
                  {stats?.periodStats?.songsPlayed || 0} songs
                </p>
              </div>
              <CalendarTodayIcon className="text-soft-violet text-3xl" />
            </div>
          </Card>
        </div>

        {/* Charts and Detailed Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Listening Time Chart */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h3 className="text-xl font-semibold text-pearl-white mb-4">
                Listening Activity - {getPeriodLabel()}
              </h3>
              <ListeningTimeChart period={selectedPeriod} />
            </Card>
          </div>

          {/* Listening Streaks */}
          <div>
            <Card className="p-6">
              <h3 className="text-xl font-semibold text-pearl-white mb-4">
                Listening Streaks
              </h3>
              <ListeningStreaks />
            </Card>
          </div>
        </div>

        {/* Top Content and Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Content Stats */}
          <TopContentStats period={selectedPeriod} />

          {/* Recent Activity */}
          <ActivityFeed limit={10} />
        </div>

        {/* Music Taste Summary */}
        {stats && (
          <Card className="p-6">
            <h3 className="text-xl font-semibold text-pearl-white mb-4">
              Your Music Taste Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-slate-grey text-sm">Favorite Genre</p>
                <p className="text-lg font-semibold text-pearl-white">
                  {stats.favoriteGenre || 'No data yet'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-slate-grey text-sm">Top Artist</p>
                <p className="text-lg font-semibold text-pearl-white">
                  {stats.favoriteArtist || 'No data yet'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-slate-grey text-sm">Music Journey</p>
                <p className="text-lg font-semibold text-pearl-white">
                  {stats.firstListeningDate 
                    ? `Started ${formatTime(stats.firstListeningDate)}`
                    : 'Just getting started'
                  }
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Year Wrapped Modal */}
        {showWrapped && (
          <Modal isOpen={showWrapped} onClose={() => setShowWrapped(false)} title="Year Wrapped">
            <YearWrapped onClose={() => setShowWrapped(false)} />
          </Modal>
        )}

        {/* Data Export Modal */}
        {showDataExport && (
          <Modal isOpen={showDataExport} onClose={() => setShowDataExport(false)} title="Export Data">
            <div className="bg-dark-void p-6 rounded-lg max-w-md mx-auto">
              <h3 className="text-xl font-semibold text-pearl-white mb-4">
                Export Your Data
              </h3>
              <p className="text-slate-grey mb-6">
                Download all your listening data in JSON format. This includes your listening history, 
                statistics, and preferences in compliance with GDPR regulations.
              </p>
              <div className="flex justify-end gap-4">
                <Button
                  onClick={() => setShowDataExport(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDataExport}
                  className="bg-gradient-to-r from-naia-pink to-soft-violet"
                >
                  <DownloadIcon className="mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
} 